import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import JalaliDateField from "../Components/JalaliDateField";
const AccountTransferAdd = ({ open, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleAdd, loading } = useAdd("shop/account-transfers", token);
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "account-transfer-add"],
    "shop/accounts/?page_size=all",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];

  const [fromAccountId, setFromAccountId] = useState("");
  const [toAccountId, setToAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [transferDate, setTransferDate] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const fromAccount = useMemo(
    () => accounts.find((a) => String(a.id) === String(fromAccountId)),
    [accounts, fromAccountId]
  );
  const toAccountOptions = useMemo(() => {
    if (!fromAccount) return [];
    return accounts.filter(
      (a) => a.shop === fromAccount.shop && String(a.id) !== String(fromAccount.id)
    );
  }, [accounts, fromAccount]);
  const toAccount = useMemo(
    () => toAccountOptions.find((a) => String(a.id) === String(toAccountId)),
    [toAccountOptions, toAccountId]
  );

  const { data: directRateData } = useFetchObjects(
    [
      "account-transfer-rate-direct",
      fromAccount?.shop,
      fromAccount?.currency,
      toAccount?.currency,
    ],
    fromAccount && toAccount
      ? `shop/exchange-rates/?page_size=1&shop=${fromAccount.shop}&from_currency=${fromAccount.currency}&to_currency=${toAccount.currency}`
      : "",
    token
  );

  const { data: reverseRateData } = useFetchObjects(
    [
      "account-transfer-rate-reverse",
      fromAccount?.shop,
      fromAccount?.currency,
      toAccount?.currency,
    ],
    fromAccount && toAccount
      ? `shop/exchange-rates/?page_size=1&shop=${fromAccount.shop}&from_currency=${toAccount.currency}&to_currency=${fromAccount.currency}`
      : "",
    token
  );

  const exchangeRatePreview = useMemo(() => {
    if (!fromAccount || !toAccount) return "";
    if (String(fromAccount.currency) === String(toAccount.currency)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [fromAccount, toAccount, directRateData, reverseRateData]);

  useEffect(() => {
    setExchangeRate(exchangeRatePreview || "");
  }, [exchangeRatePreview, fromAccountId, toAccountId]);

  const convertedAmountPreview = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  const handleSubmit = () => {
    handleAdd({
      from_account: fromAccountId,
      to_account: toAccountId,
      amount,
      exchange_rate: exchangeRate,
      reference: reference.trim() || null,
      note: note.trim() || null,
      transfer_date: transferDate,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("accountTransfer.addTitle")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            select
            margin="dense"
            fullWidth
            required
            label={t("accountTransfer.fromAccount")}
            value={fromAccountId}
            onChange={(e) => {
              setFromAccountId(e.target.value);
              setToAccountId("");
            }}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              accounts.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>
                  {a.name} ({a.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>
          {fromAccount && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, textAlign: fieldTextAlign }}>
              {t("accountTransfer.currentBalance", "Current Balance")}: {fromAccount.balance} {fromAccount.currency_title}
            </Typography>
          )}

          <TextField
            select
            margin="dense"
            fullWidth
            required
            disabled={!fromAccount}
            label={t("accountTransfer.toAccount")}
            value={toAccountId}
            onChange={(e) => setToAccountId(e.target.value)}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {toAccountOptions.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.name} ({a.currency_title || "-"})
              </MenuItem>
            ))}
          </TextField>
          {toAccount && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, textAlign: fieldTextAlign }}>
              {t("accountTransfer.currentBalance", "Current Balance")}: {toAccount.balance} {toAccount.currency_title}
            </Typography>
          )}

          <TextField
            margin="dense"
            fullWidth
            required
            type="number"
            label={t("accountTransfer.amount")}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            {...getRtlTextFieldProps(formDirection, {
              inputProps: { step: "0.01", min: "0.01" },
            })}
          />

          <TextField
            margin="dense"
            fullWidth
            type="number"
            label={t("accountTransfer.exchangeRate")}
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            disabled={!fromAccount || !toAccount}
            {...getRtlTextFieldProps(formDirection, {
              inputProps: { step: "0.000001", min: "0.000001" },
            })}
          />

          <TextField
            margin="dense"
            fullWidth
            disabled
            label={t("accountTransfer.convertedAmount")}
            value={convertedAmountPreview}
            {...getRtlTextFieldProps(formDirection)}
          />

          {!exchangeRate && fromAccount && toAccount && (
            <Alert severity="warning" sx={{ mt: 1, textAlign: fieldTextAlign }}>
              {t("accountTransfer.missingRate")}{" "}
              {t("accountTransfer.enterRateManually", "You can enter exchange rate manually.")}
            </Alert>
          )}

          <TextField
            margin="dense"
            fullWidth
            label={t("accountTransfer.reference")}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />

          <TextField
            margin="dense"
            fullWidth
            multiline
            rows={3}
            label={t("accountTransfer.note")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />

          <JalaliDateField mode="date"
            margin="dense"
            fullWidth
            required
            label={t("accountTransfer.transferDate")}
            value={transferDate}
            onChange={(e) => setTransferDate(e.target.value)}
            {...getRtlTextFieldProps(formDirection, {
              InputLabelProps: { shrink: true },
            })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {t("accountTransfer.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !fromAccountId ||
            !toAccountId ||
            !amount ||
            Number(amount) <= 0 ||
            !exchangeRate ||
            Number(exchangeRate) <= 0
          }
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : t("accountTransfer.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountTransferAdd;
